'use client';

import {
  BotMessageSquare,
  Files,
  Home,
  Info,
  LayoutDashboard,
  MessageSquareText,
  MessagesSquare,
  Settings,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/clerk-react';

const mainNav = [
  {
    title: 'Home',
    icon: Home,
    href: '/',
  },
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    href: '/dashboard',
  },
];

const documentsNav = [
  {
    title: 'All Documents',
    icon: Files,
    href: '/documents',
  },
  {
    title: 'Character Chats',
    icon: MessagesSquare,
    href: '/character-doc-chats',
  },
];

// AI navigation items with special handling for "New AI chat"
const aiNav = [
  {
    title: 'New AI chat',
    icon: BotMessageSquare,
    href: '/ai-chat',
    forceReload: true, // Flag to indicate we want to force a reload
  },
  {
    title: 'Past chats',
    icon: MessageSquareText,
    href: '/ai-chats',
  },
];

export function AppSidebar() {
  const location = useLocation();
  const { subscriptionSpend, subscriptionLimit } = useSubscription();

  return (
    <Sidebar>
      <SidebarHeader></SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={location.pathname === item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        'flex items-center gap-2',
                        location.pathname === item.href && 'text-accent-foreground'
                      )}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Documents</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {documentsNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={location.pathname === item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        'flex items-center gap-2',
                        location.pathname === item.href && 'text-accent-foreground'
                      )}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>AI</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {aiNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={location.pathname === item.href}>
                    {item.forceReload ? (
                      <Link
                        to={`${item.href}?new=true`}
                        className={cn(
                          'flex items-center gap-2',
                          location.pathname === item.href && 'text-accent-foreground'
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    ) : (
                      <Link
                        to={item.href}
                        className={cn(
                          'flex items-center gap-2',
                          location.pathname === item.href && 'text-accent-foreground'
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Settings</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === '/settings'}>
                  <Link
                    to="/settings"
                    className={cn(
                      'flex items-center gap-2',
                      location.pathname === '/settings' && 'text-accent-foreground'
                    )}
                  >
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div id="credit-indicator" className="w-full px-3 mb-4">
          <div id="credit-info" className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
            <Info className="h-3 w-3" />
            <HoverCard>
              <HoverCardTrigger asChild>
                <span className="cursor-help">Credits:</span>
              </HoverCardTrigger>
              <HoverCardContent className="w-56">
                <p className="text-sm">Current credit usage for this period</p>
              </HoverCardContent>
            </HoverCard>
          </div>
          <div className="w-full text-center">
            <div className="text-sm font-medium">
              {subscriptionSpend} / {subscriptionLimit}
            </div>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mt-1">
              <div
                className={cn(
                  'h-full rounded-full',
                  subscriptionSpend / subscriptionLimit > 0.95 ? 'bg-destructive' : 'bg-primary'
                )}
                style={{
                  width: `${Math.min((subscriptionSpend / subscriptionLimit) * 100, 100)}%`,
                }}
              />
            </div>
            {subscriptionSpend > subscriptionLimit && (
              <div className="mt-2 text-xs text-destructive font-medium">AI use requires credits</div>
            )}
          </div>
        </div>
        <div className="flex justify-center">
          <SignedIn>
            <UserButton showName={true} />
          </SignedIn>
          <SignedOut>
            <SignInButton />
          </SignedOut>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
